import {
	apiClient,
	Button,
	ButtonGroup,
	DropdownFormInput,
	parser,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import widgetApi from "../../api/widgetApi";
import getChangeDates from "../../dateUtils";
import { useAuthenticatedAuthContext } from "../auth/Auth.component";

interface FormData {
	expirationDate: string;
}

const CancelSubscriptionPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const startDates = getChangeDates();
	const { subscriptions, token, userData, refetchSubscriptions } =
		useAuthenticatedAuthContext();
	const [loading, setLoading] = useState(false);

	const formMethods = useForm<FormData>({
		defaultValues: {
			expirationDate: startDates[0],
		},
	});

	const subscription = subscriptions.find((x) => x.id === id);

	if (!subscription) {
		throw new Error(`Could not find ticket with id=${id}`);
	}

	const isActiveSubscription = subscription.state === "ACTIVE";

	const cancelSubscription = async (formData: FormData) => {
		setLoading(true);
		if (isActiveSubscription) {
			const response = await apiClient.request(
				widgetApi.subscriptions.cancelActive(
					token,
					userData,
					subscription.id,
					parser.date.fromShortDateString(formData.expirationDate),
				),
			);
			if (response.error) {
				setLoading(() => {
					throw response.error;
				});
				return;
			}
		} else {
			const response = await apiClient.request(
				widgetApi.subscriptions.cancelPending(token, userData, subscription.id),
			);
			if (response.error) {
				setLoading(() => {
					throw response.error;
				});
				return;
			}
		}
		navigate("/abonnement");
		await refetchSubscriptions();
	};

	if (subscription.type === "JOULE") {
		return (
			<div data-test-id="cancel-joule-page">
				<h1>Hva må du gjøre hvis du ønsker å avslutte abonnementet?</h1>

				<p>
					For å avslutte abonnementet ditt logger du inn på din{" "}
					<a
						href="https://login.joule.no/no/auth/login"
						target="_blank"
						rel="noreferrer"
					>
						brukerprofil
					</a>{" "}
					som du opprettet hos Joule og trykker på &quot;avslutt
					abonnement&quot;. Du vil deretter få en melding der du velger når og
					hvordan du ønsker å levere inn elsykkelen din. Tilbakelevering av
					elsykkel skal skje før leieperioden din utløper. Hvis dette ikke
					skjer, vil abonnementet fornyes automatisk.
				</p>
				<ButtonGroup>
					<Button
						data-test-id="cancel-button"
						skin="cancel"
						onClick={() => {
							navigate("/abonnement");
						}}
						type="button"
					>
						Tilbake
					</Button>
				</ButtonGroup>
			</div>
		);
	}
	if (subscription.type === "CAR_POOL") {
		return (
			<div data-test-id="cancel-car-rental-page">
				<h1>Avslutt leiebil</h1>

				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(cancelSubscription)}>
						{isActiveSubscription && (
							<DropdownFormInput
								name="expirationDate"
								label="Leiebil er gyldig til"
							>
								{startDates.map((date) => (
									<option key={date} value={date}>
										{date}
									</option>
								))}
							</DropdownFormInput>
						)}

						<p>
							Er du sikker på at du ønsker å avslutte abonenement på leiebil?
						</p>
						<ButtonGroup>
							<Button
								data-test-id="confirm-button"
								skin="danger"
								type="submit"
								loading={loading}
							>
								Ja
							</Button>
							<Button
								data-test-id="cancel-button"
								skin="cancel"
								onClick={() => {
									navigate("/abonnement");
								}}
								type="button"
							>
								Avbryt
							</Button>
						</ButtonGroup>
					</form>
				</FormProvider>
			</div>
		);
	}
};

export default CancelSubscriptionPage;
